import { reactive } from 'vue'
import { closeAlertPopup } from './sheet-alert'

/*
 * 시트 위에 뜨는 것들 — 구 V2ReservationManagementDataController 에서 팝업·메뉴 표시를
 * 떼어냈다 (MDP-2295 ④-5 5단계).
 *
 * 하는 일은 단순하다: 무엇을 띄우고 무엇을 닫을지, 그리고 띄울 때 필요한 값
 * (어디에 띄울지, 무엇을 대상으로, 닫힌 뒤 무엇을 부를지)을 담는다.
 *
 * 규칙 세 가지가 코드에 박혀 있어 그대로 옮겼다.
 *   1) 큰 팝업(고객정보·예약·검색·엑셀저장)은 서로 배타적이다 — 하나를 띄우면
 *      closePopup() 이 나머지를 닫는다.
 *   2) 그룹 메뉴와 담당자 메뉴는 서로 배타적이다 — 한쪽을 띄우면 다른 쪽을 닫는다.
 *   3) 설정·메뉴를 닫을 때는 대상(selectedGroup/selectedColumn)과 콜백도 함께 비운다.
 *      남겨 두면 다음에 열릴 때 이전 대상이 잠깐 보인다.
 *
 * 큰 팝업을 닫을 때는 알럿도 함께 닫는다(sheet-alert).
 */

type Handler = ((...args: never[]) => unknown) | undefined

export const sheetOverlays = reactive({
    // 큰 팝업 (서로 배타적)
    isShowUserInfoPopup: false,
    isShowReservationPopup: false,
    isShowExcelSavePopup: false,
    isShowHServiceSearch: false,

    // 우클릭 메뉴
    isShowReservationMenu: false,
    isShowGroupMenu: false,
    isShowColumnMenu: false,
    isShowCellMenu: false,

    // 설정·부가 팝업
    isShowGroupSetting: false,
    isShowColumnSetting: false,
    isShowAIMessageEditPopup: false,
    isShowAIDetailEditPopup: false,
    isShowMonitorPopup: false,
    isShowReservationHistoryPopup: false,
    isShowReservationDateNotePopup: false,
    isShowWorkStatePopup: false,
    isShowColorEditPopup: false,
    isShowReservationBlockPopup: false,
    isShowBlockRepeatOptionPopup: false,

    // 띄울 자리
    reservationMenuPosition: undefined as unknown,
    groupMenuPosition: undefined as unknown,
    columnMenuPosition: undefined as unknown,
    cellMenuPosition: undefined as unknown,
    lastEventPosition: undefined as unknown,
    lastTimeData: undefined as unknown,

    // 대상
    selectedGroup: undefined as unknown,
    selectedColumn: undefined as unknown,
    selectedReservationBlock: {} as Record<string, unknown>,
    selectedColorCell: undefined as unknown,
    selectedRangeCells: [] as unknown[],
    isDiffBlockRepeatOption: undefined as unknown,

    // 닫힌 뒤 부를 것
    groupSettingHandler: undefined as Handler,
    columnSettingHandler: undefined as Handler,
    repeatOptionPopupHandler: undefined as Handler,
})

// ─────────────────────── 큰 팝업 (서로 배타적) ───────────────────────

export function closePopup(): void {
    closeAlertPopup()
    sheetOverlays.isShowUserInfoPopup = false
    sheetOverlays.isShowReservationPopup = false
    sheetOverlays.isShowHServiceSearch = false
    sheetOverlays.isShowExcelSavePopup = false
}

export function showUserInfoPopup(): void {
    closePopup()
    sheetOverlays.isShowUserInfoPopup = true
}

export function showReservationInfoPopup(): void {
    closePopup()
    sheetOverlays.isShowReservationPopup = true
}

export function showExcelSavePopup(): void {
    closePopup()
    sheetOverlays.isShowExcelSavePopup = true
}

/** 빈 칸에서 고객을 찾는 검색 레이어 — 어느 칸에서 열었는지 함께 기억한다 */
export function showHServiceSearch(position: unknown, timeData: unknown): void {
    sheetOverlays.isShowHServiceSearch = true
    sheetOverlays.lastEventPosition = position
    sheetOverlays.lastTimeData = timeData
}

export function closeHServiceSearch(): void {
    sheetOverlays.isShowHServiceSearch = false
    sheetOverlays.lastEventPosition = undefined
}

// ─────────────────────────── 우클릭 메뉴 ───────────────────────────

export function showReservationMenu(): void {
    sheetOverlays.isShowReservationMenu = true
}

export function hideReservationMenu(): void {
    sheetOverlays.isShowReservationMenu = false
}

/** 그룹 메뉴 — 담당자 메뉴와 동시에 뜨지 않는다 */
export function showGroupMenu(position: unknown, group: unknown): void {
    hideColumnMenu()
    sheetOverlays.selectedGroup = group
    sheetOverlays.groupMenuPosition = position
    sheetOverlays.isShowGroupMenu = true
}

export function hideGroupMenu(): void {
    sheetOverlays.isShowGroupMenu = false
    sheetOverlays.groupMenuPosition = undefined
}

/** 담당자 메뉴 — 그룹 메뉴와 동시에 뜨지 않는다 */
export function showColumnMenu(position: unknown, column: unknown): void {
    hideGroupMenu()
    sheetOverlays.selectedColumn = column
    sheetOverlays.columnMenuPosition = position
    sheetOverlays.isShowColumnMenu = true
}

export function hideColumnMenu(): void {
    sheetOverlays.isShowColumnMenu = false
    sheetOverlays.columnMenuPosition = undefined
}

/** 셀 메뉴는 열리는 순간의 범위를 복사해 들고 있다 — 뒤늦게 데이터가 와도 바뀌지 않는다 */
export function showCellMenu(position: unknown, data: unknown[]): void {
    sheetOverlays.cellMenuPosition = position
    sheetOverlays.selectedRangeCells = data
    sheetOverlays.isShowCellMenu = true
}

export function hideCellMenu(): void {
    sheetOverlays.isShowCellMenu = false
    sheetOverlays.cellMenuPosition = undefined
    sheetOverlays.selectedRangeCells = []
}

// ────────────────────────── 설정·부가 팝업 ──────────────────────────

export function showGroupSetting(group: unknown, handler?: Handler): void {
    sheetOverlays.selectedGroup = group
    sheetOverlays.groupSettingHandler = handler
    sheetOverlays.isShowGroupSetting = true
}

export function hideGroupSetting(): void {
    sheetOverlays.isShowGroupSetting = false
    sheetOverlays.selectedGroup = undefined
    sheetOverlays.groupSettingHandler = undefined
}

export function showColumnSetting(column: unknown, handler?: Handler): void {
    sheetOverlays.selectedColumn = column
    sheetOverlays.columnSettingHandler = handler
    sheetOverlays.isShowColumnSetting = true
}

export function hideColumnSetting(): void {
    sheetOverlays.selectedColumn = undefined
    sheetOverlays.isShowColumnSetting = false
    sheetOverlays.columnSettingHandler = undefined
}

export function showAIMessageEditPopup(group: unknown): void {
    sheetOverlays.selectedGroup = group
    sheetOverlays.isShowAIMessageEditPopup = true
}

export function hideAIMessageEditPopup(): void {
    sheetOverlays.isShowAIMessageEditPopup = false
    sheetOverlays.selectedGroup = undefined
}

export function showAIDetailEditPopup(column: unknown): void {
    sheetOverlays.selectedColumn = column
    sheetOverlays.isShowAIDetailEditPopup = true
}

export function hideAIDetailEditPopup(): void {
    sheetOverlays.isShowAIDetailEditPopup = false
    sheetOverlays.selectedColumn = undefined
}

export function showMonitorPopup(): void {
    sheetOverlays.isShowMonitorPopup = true
}

export function closeMonitorPopup(): void {
    sheetOverlays.isShowMonitorPopup = false
}

export function showReservationHistoryPopup(): void {
    sheetOverlays.isShowReservationHistoryPopup = true
}

export function closeReservationHistoryPopup(): void {
    sheetOverlays.isShowReservationHistoryPopup = false
}

export function showReservationDateNotePopup(): void {
    sheetOverlays.isShowReservationDateNotePopup = true
}

export function closeReservationDateNotePopup(): void {
    sheetOverlays.isShowReservationDateNotePopup = false
}

export function showWorkStatePopup(): void {
    sheetOverlays.isShowWorkStatePopup = true
}

export function closeWorkStatePopup(): void {
    sheetOverlays.isShowWorkStatePopup = false
}

export function showColorEditPopup(cellData: unknown): void {
    sheetOverlays.selectedColorCell = cellData
    sheetOverlays.isShowColorEditPopup = true
}

export function closeColorEditPopup(): void {
    sheetOverlays.selectedColorCell = undefined
    sheetOverlays.isShowColorEditPopup = false
}

export function showReservationBlockPopup(blockData: Record<string, unknown>): void {
    sheetOverlays.selectedReservationBlock = blockData
    sheetOverlays.isShowReservationBlockPopup = true
}

export function closeReservationBlockPopup(): void {
    sheetOverlays.selectedReservationBlock = {}
    sheetOverlays.isShowReservationBlockPopup = false
}

export function showBlockRepeatOptionPopup(isDiff: unknown, handler?: Handler): void {
    sheetOverlays.isShowBlockRepeatOptionPopup = true
    sheetOverlays.isDiffBlockRepeatOption = isDiff
    sheetOverlays.repeatOptionPopupHandler = handler
}

export function closeBlockRepeatOptionPopup(): void {
    sheetOverlays.isShowBlockRepeatOptionPopup = false
    sheetOverlays.isDiffBlockRepeatOption = undefined
    sheetOverlays.repeatOptionPopupHandler = undefined
}
